import {useEffect, useMemo, useState} from 'react';
import {usePrevious, useUpdateEffect} from 'primereact/hooks';

import {AnalyticsEvent, TRACKING_FLOW} from '@/types/tracking';

import {useUser} from '@/contexts/UserContext';
import {tracking} from '@/services/tracking/TrackingService';

import type {EventsKey} from '@/services/tracking/analytics/event';

// Based on the config given, decides to format an AnalyticsEvent
// - If eventName is given, it will use it to create the event
const useExtractAnalyticEvent = ({state, eventName}: {state: any; eventName?: EventsKey}) => {
  const finalEvent = useMemo(() => {
    if (!eventName) {
      return null;
    }
    return tracking.events[eventName](state);
  }, [eventName, state]);

  return finalEvent;
};

export const useTrackedState = <T extends any = string>({
  initialState,
  eventName,
  logEveryStateChange,
}: {
  initialState: any;
  eventName: EventsKey;
  logEveryStateChange?: boolean;
}) => {
  const {fbUser} = useUser();
  const [triggered, setTriggered] = useState<boolean>(false);
  const [state, setState] = useState<T>(initialState);
  const finalEvent = useExtractAnalyticEvent({
    state,
    eventName,
  });

  const prevState = usePrevious(state);
  useUpdateEffect(() => {
    if (fbUser && state && finalEvent && !triggered) {
      setTriggered(true);
      tracking.logEvent(finalEvent).then();
    } else if (!state || (logEveryStateChange && prevState !== state)) {
      setTriggered(false);
    }
  }, [fbUser, finalEvent, state, triggered]);

  return [state, setState] as const;
};

export const useTrackStateOnce = <T extends any = string>({
  initialState,
  eventName,
}: {
  initialState: any;
  eventName?: EventsKey;
}) => {
  const [state, setState] = useState<T>(initialState);
  const [triggered, setTriggered] = useState(false);
  const finalEvent = useExtractAnalyticEvent({
    state,
    eventName,
  });

  useUpdateEffect(() => {
    if (state && finalEvent && !triggered) {
      tracking.logEvent(finalEvent).then();
      setTriggered(true);
    }
  }, [eventName, finalEvent, state]);

  return [state, setState] as const;
};

export const useTrackedErrorState = ({name}: {name: TRACKING_FLOW}) => {
  const [state, setState] = useState<string | undefined>(undefined);

  useErrorEvent({name, error: state});

  return [state, setState] as const;
};

export const useErrorEvent = ({name, error}: {name: TRACKING_FLOW; error?: string}) => {
  const [previousError, setPreviousError] = useState('');

  useEffect(() => {
    if (error && error !== previousError) {
      setPreviousError(error);
      tracking
        .logEvent({
          name,
          params: {
            errorMessage: error,
          },
        })
        .then();
    }
  }, [name, error, previousError]);
};

export const useEvent = (event?: AnalyticsEvent) => {
  const [triggered, setTriggered] = useState(false);

  useEffect(() => {
    if (!triggered && event) {
      tracking.logEvent({...event}).then();
      setTriggered(true);
    }
  }, [event, triggered]);
};
